/**
 * MapCard Component
 * 
 * This component renders a card for displaying farm information on a map.
 * It fetches the cover image for the farm and displays it along with the farm's name
 * 
 * @author FarmCiti Team
 * @version 1.0.0
 */

import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import MapCardStyle from "../../assets/stylesheets/map/MapCard.module.css";
import SkeletonCard from '../common/SkeletonCard';
import Logger from "../../utils/logger";

const log = Logger.create("MapCard");

export default function MapCard({ 
  farm, 
  index,
  onImageLoaded, 
  onClick, 
  onClose 
}) {
  const [imageLoaded, setImageLoaded] = useState(false);

  // Get cover image URL from farm data or use fallback
  const coverUrl = farm.coverImage?.viewUrl || "/assets-placeholder/no-image.png";

  // Image preload
  useEffect(() => {
    if (!coverUrl) return;
    
    const img = new Image();
    img.src = coverUrl;
    img.onload = () => {
      setImageLoaded(true);
      onImageLoaded?.(index);
    };
    img.onerror = () => {
      setImageLoaded(true);
      onImageLoaded?.(index);
    };
    
    return () => {
      img.onload = null;
      img.onerror = null;
    };
  }, [coverUrl, index, onImageLoaded]);

  if (!imageLoaded) {
    return (
      <SkeletonCard 
        variant="map-card"
        mapTitleClass={MapCardStyle["map-card-title"]}
        mapLocationClass={MapCardStyle["map-card-location"]}
      />
    );
  }
    
  return (
    <div className={MapCardStyle["map-card"]} onClick={onClick}>
      <div className={MapCardStyle["map-card-image-container"]}>
        
        <img
          src={coverUrl}
          alt={farm.name}
          className={MapCardStyle["map-card-image"]}
        />
        <button
          className={MapCardStyle["map-card-close"]}
          onClick={(e) => {
            e.stopPropagation();
            onClose();
          }}
          aria-label="Close"
        >
          ×
        </button>
      </div>
      <div className={MapCardStyle["map-card-info"]}>
        <h3 className={MapCardStyle["map-card-title"]}>{farm.name}</h3>
        <p className={MapCardStyle["map-card-location"]}>
          {farm.location?.address?.city  || "Unknown city"},&nbsp;
          {farm.location?.address?.state || "Unknown state"}      
        </p>      
      </div>
    </div>
  );
}

MapCard.propTypes = {
  farm: PropTypes.shape({
    id: PropTypes.number.isRequired,
    name: PropTypes.string.isRequired,
    coverImage: PropTypes.shape({
      viewUrl: PropTypes.string,
    }),
    location: PropTypes.shape({
      address: PropTypes.shape({
        city: PropTypes.string,
        state: PropTypes.string,
      }).isRequired,
    }).isRequired,
  }).isRequired,
  index: PropTypes.number,
  onImageLoaded: PropTypes.func,
  onClick: PropTypes.func,
  onClose: PropTypes.func.isRequired
};
